import { TextAreaInput } from '@/components/form/TextAreaInput';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { AvailabilityExceptionLeaveRequestDetailsCard } from '@/pages/ess/myRequests/AvailabilityExceptionLeaveRequestDetailsCard';
import { useAvailabilityExceptionRequest } from '@/services/availability/useAvailabilityExceptionRequest';
import { Feature } from '@/services/backstage/Feature';
import {
  ApprovalState,
  useApproveAvailabilityExceptionRequestMutation,
  useManagerCancelAvailabilityExceptionRequestMutation,
  useRejectAvailabilityExceptionRequestMutation
} from '@/services/gql/graphql.generated';

import { strings } from '@/services/translation/strings';
import { FunctionComponent, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

const TeamMemberAvailabilityRequestDetailsPage: FunctionComponent = () => {
  const { userId, requestId } = useParams<'userId' | 'requestId'>();
  const navigate = useNavigate();
  const { request, refetch } = useAvailabilityExceptionRequest(userId, requestId);
  const [approveResult, approveRequest] = useApproveAvailabilityExceptionRequestMutation();
  const [rejectResult, rejectRequest] = useRejectAvailabilityExceptionRequestMutation();
  const [cancelResult, cancelRequest] = useManagerCancelAvailabilityExceptionRequestMutation();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelLeaveDialogOpen, setCancelLeaveDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  if (!request) {
    return <p className="p-6">Availability request not found.</p>;
  }
  const exception = request.exceptions?.[0];
  const isLeaveRequest = !!exception?.leaveType;
  const isPending = request.requestState === ApprovalState.Requested;
  const isApprovedLeave =
    isLeaveRequest &&
    (request.requestState === ApprovalState.Approved || request.requestState === ApprovalState.Applied);
  const isRequestPast = () => request.end < dateToApiFormat(new Date());
  const isPastRequest = isRequestPast();
  const isUpdating = approveResult.fetching || rejectResult.fetching || cancelResult.fetching;
  const requestStrings = isLeaveRequest
    ? strings.timeshift.teamMember.requests.leaveRequest
    : strings.timeshift.teamMember.requests.availabilityRequest;

  const approveRequestHandler = async () => {
    if (!userId || !requestId || !isPending || isUpdating || isRequestPast()) return;
    const result = await approveRequest({
      availabilityExceptionRequestApproval: { requestId, employeeId: userId }
    });
    await handleMutationResult(result, {
      successMessage: requestStrings.approveSuccess,
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate(-1), 100);
      }
    });
  };

  const rejectRequestHandler = async () => {
    if (!userId || !requestId || !isPending || isUpdating || isRequestPast()) return;
    const result = await rejectRequest({
      availabilityExceptionRequestRejection: {
        requestId,
        employeeId: userId,
        rejectionReason: rejectionReason.trim() || undefined
      }
    });
    await handleMutationResult(result, {
      successMessage: isLeaveRequest ? 'Leave request denied.' : 'Availability change denied.',
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate(-1), 100);
      }
    });
  };

  const cancelLeaveHandler = async () => {
    if (!userId || !requestId || !isApprovedLeave || isUpdating || isRequestPast()) return;
    const result = await cancelRequest({
      availabilityExceptionRequestCancellation: {
        requestId,
        employeeId: userId,
        rejectionReason: cancellationReason.trim() || undefined
      }
    });
    await handleMutationResult(result, {
      successMessage: 'Leave cancelled.',
      onSuccess: async () => {
        setCancelLeaveDialogOpen(false);
        setCancellationReason('');
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate(-1), 100);
      }
    });
  };

  const renderActionButtons = () => {
    if (isPending && !isPastRequest) {
      return (
        <>
          <Button big danger disabled={isUpdating} onClick={() => setRejectDialogOpen(true)}>
            {strings.common.deny}
          </Button>
          <Button big primary disabled={isUpdating} onClick={() => setApproveDialogOpen(true)}>
            {strings.common.approve}
          </Button>
        </>
      );
    }
    if (isLeaveRequest) {
      return isApprovedLeave && !isPastRequest ? (
        <Button big danger disabled={isUpdating} onClick={() => setCancelLeaveDialogOpen(true)}>
          Cancel Leave
        </Button>
      ) : null;
    }
    return null;
  };

  return (
    <Feature name="requests">
      <PageContainer>
        <PageSizer mode={SizerMode.PAGE} className="justify-items-center">
          <TSTitleCard className="w-full space-y-5 p-5" title={requestStrings.pageTitle} icon={FiBell}>
            <AvailabilityExceptionLeaveRequestDetailsCard
              request={request}
              actions={
                <div className="flex space-x-4 justify-end">
                  <Button big onClick={() => navigate(-1)}>
                    {strings.common.back}
                  </Button>
                  {renderActionButtons()}
                </div>
              }
            />
          </TSTitleCard>
        </PageSizer>
      </PageContainer>
      <ConfirmDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={approveRequestHandler}
        title={requestStrings.approveTitle}
        confirmLabel={strings.common.approve}
        cancelLabel={strings.common.cancel}
        loading={approveResult.fetching}
      >
        {requestStrings.approveMessage}
      </ConfirmDialog>
      <ConfirmDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectionReason('');
        }}
        onConfirm={rejectRequestHandler}
        title={requestStrings.rejectTitle}
        confirmLabel={strings.common.deny}
        cancelLabel={strings.common.cancel}
        dangerous
        loading={rejectResult.fetching}
      >
        <div className="space-y-3">
          <p>{requestStrings.rejectMessage}</p>
          <div>
            <label htmlFor="rejection-reason" className="text-sm font-medium block mb-1">
              Reason (optional)
            </label>
            <TextAreaInput
              name="rejection-reason"
              placeholder="Let them know why this request is being denied…"
              value={rejectionReason}
              onChange={setRejectionReason}
              rows={3}
            />
          </div>
        </div>
      </ConfirmDialog>
      {isLeaveRequest && (
        <ConfirmDialog
          open={cancelLeaveDialogOpen}
          onClose={() => {
            setCancelLeaveDialogOpen(false);
            setCancellationReason('');
          }}
          onConfirm={cancelLeaveHandler}
          title="Cancel leave"
          confirmLabel="Cancel Leave"
          cancelLabel={strings.common.back}
          dangerous
          loading={cancelResult.fetching}
        >
          <div className="space-y-3">
            <p>
              Are you sure? All leave for the requested period will be cancelled. This will be recorded as cancelled by
              manager.
            </p>
            <div>
              <label htmlFor="cancellation-reason" className="text-sm font-medium block mb-1">
                Reason (optional)
              </label>
              <TextAreaInput
                name="cancellation-reason"
                placeholder="Let them know why this leave is being cancelled…"
                value={cancellationReason}
                onChange={setCancellationReason}
                rows={3}
              />
            </div>
          </div>
        </ConfirmDialog>
      )}
    </Feature>
  );
};
export default TeamMemberAvailabilityRequestDetailsPage;
